import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ClipboardList, Pencil, RefreshCw, Rss } from 'lucide-react';

import { observer } from 'mobx-react';
import { injectTSDI } from '@core/tsdi';
import { Api } from '@core/api';

import { links } from '@core/router';
import { getCdnImage } from '@components/utils/cdn-image';
import {
    getStreamBetaDashboardUrl,
    getFastrelayFeedUrl
} from '@components/utils/stream-utils';
import { FormSection } from '@components/form-section';
import { Tooltip } from '@components/tooltip';
import { Button } from '@components/ui/button';
import { CommunityContext } from '@src/communities/community';
import { I18NText } from '@api/ChallengeClient';
import { useCommunitySettingsStore } from './store';

const Field = ({
    label,
    children,
    stacked
}: {
    label: string;
    children: React.ReactNode;
    stacked?: boolean;
}) =>
    stacked ? (
        <div className="col-span-2 flex flex-col gap-1 pt-2">
            <dt className="text-muted-foreground">{label}</dt>
            <dd>{children}</dd>
        </div>
    ) : (
        <>
            <dt className="text-muted-foreground">{label}</dt>
            <dd>{children}</dd>
        </>
    );

const CommunityDetails = observer(() => {
    const { communityId } = useParams();

    const navigate = useNavigate();

    const { env } = injectTSDI(Api);

    const {
        communitySettings,
        triggerTenantMemberSync,
        handleTriggerTenantMemberSync
    } = useCommunitySettingsStore();

    const communityDetails = React.useContext(CommunityContext);

    React.useEffect(() => {
        if (communityId) {
            communitySettings.request([parseInt(communityId)]);
        }
    }, [communityId]);

    const fastrelayEnabled = communitySettings.$?.fastrelayEnabled;
    const description = communityDetails?.description;
    const hasTranslation =
        description && (description.en || description.de || description.pl);

    return (
        <div>
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                {communityDetails.photo ? (
                    <img
                        src={getCdnImage(
                            communityDetails.photo.bucketName,
                            communityDetails.photo.objectKey,
                            {
                                resize: {
                                    width: 320,
                                    height: 180,
                                    fit: 'cover'
                                }
                            }
                        )}
                        alt={`${communityDetails.name || 'Community'} photo`}
                        className="h-[120px] w-auto rounded-lg border object-contain"
                    />
                ) : (
                    <div />
                )}
                <div className="flex flex-wrap items-center gap-2">
                    {communityDetails?.chatId && fastrelayEnabled === false && (
                        <>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    const filters = encodeURIComponent(
                                        JSON.stringify([
                                            {
                                                key: 'id',
                                                value: [communityDetails.chatId]
                                            }
                                        ])
                                    );
                                    const url = getStreamBetaDashboardUrl(
                                        `/moderation/feed-explorer/?filters=${filters}`,
                                        env
                                    );
                                    window.open(url, '_blank');
                                }}
                            >
                                <Rss />
                                Feed Moderation
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    const url = getStreamBetaDashboardUrl(
                                        `/feeds/explorer/community:${communityDetails.chatId}`,
                                        env
                                    );
                                    window.open(url, '_blank');
                                }}
                            >
                                <Rss />
                                Feed Explorer
                            </Button>
                        </>
                    )}
                    {communityDetails?.chatId && fastrelayEnabled === true && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                const url = getFastrelayFeedUrl(
                                    `community:${communityDetails.chatId}`,
                                    env
                                );
                                window.open(url, '_blank');
                            }}
                        >
                            <Rss />
                            Feed View
                        </Button>
                    )}
                    <Button
                        size="sm"
                        onClick={() =>
                            navigate(links.editCommunity(communityId!))
                        }
                    >
                        <Pencil />
                        Edit
                    </Button>
                </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
                <FormSection title="Basic Information">
                    <dl className="grid grid-cols-[160px_1fr] gap-y-2 text-sm">
                        <Field label="Community ID">
                            {communityDetails.id || 'N/A'}
                        </Field>
                        <Field label="Name">
                            {communityDetails.name || 'N/A'}
                        </Field>
                        <Field label="Tenant ID">
                            {communityDetails.tenantId || 'N/A'}
                        </Field>
                        <Field label="Description" stacked>
                            {description ? (
                                <div className="flex flex-col gap-1">
                                    {description.en && (
                                        <div>
                                            <span className="text-xs text-muted-foreground">
                                                English:
                                            </span>
                                            <p>
                                                {extractDescriptionText(
                                                    description
                                                )}
                                            </p>
                                        </div>
                                    )}
                                    {description.de && (
                                        <div>
                                            <span className="text-xs text-muted-foreground">
                                                German:
                                            </span>
                                            <p>{description.de}</p>
                                        </div>
                                    )}
                                    {description.pl && (
                                        <div>
                                            <span className="text-xs text-muted-foreground">
                                                Polish:
                                            </span>
                                            <p>{description.pl}</p>
                                        </div>
                                    )}
                                    {!hasTranslation && (
                                        <p>
                                            No description available in
                                            supported languages
                                        </p>
                                    )}
                                </div>
                            ) : (
                                'No description available'
                            )}
                        </Field>
                        <Field label="Type">
                            {communityDetails.type || 'N/A'}
                        </Field>
                        <Field label="Member Limit">
                            {communityDetails?.memberLimit || 'N/A'}
                        </Field>
                        <Field label="Currency">
                            {communityDetails.currencyCode || 'N/A'}
                        </Field>
                        <Field label="Push notifications">
                            {communityDetails.pushNotificationEnabled
                                ? 'Enabled'
                                : 'Disabled'}
                        </Field>
                        <Field label="Welcome message">
                            {communityDetails.welcomeMessageEnabled
                                ? 'Enabled'
                                : 'Disabled'}
                        </Field>
                        <Field label="Tenant auto-join">
                            {communityDetails.tenantMemberAutoJoinEnabled
                                ? 'Enabled'
                                : 'Disabled'}
                        </Field>
                        <Field label="Supported languages">
                            {communityDetails.supportedLanguages
                                .map((it) => it.toString())
                                .join(',')}
                        </Field>
                        <Field label="Locale">
                            {communityDetails.locale || 'N/A'}
                        </Field>
                        {communityDetails.joinConsents?.length > 0 && (
                            <Field label="Consents">
                                <span className="inline-flex items-center gap-1">
                                    <ClipboardList className="size-4 text-primary" />
                                    Required (
                                    {communityDetails.joinConsents.length})
                                </span>
                            </Field>
                        )}
                    </dl>
                </FormSection>

                <FormSection title="Status">
                    <dl className="grid grid-cols-[160px_1fr] gap-y-2 text-sm">
                        <Field label="Status">
                            {getStatusText(communityDetails.approved)}
                        </Field>
                        <Field label="Visibility">
                            {communityDetails.visibility || 'N/A'}
                        </Field>
                        <Field label="License Status">
                            {communityDetails.licenseExpired
                                ? 'Expired'
                                : 'Active'}
                        </Field>
                        <Field label="Created Date">
                            {formatDate(communityDetails.createdDate)}
                        </Field>
                    </dl>
                </FormSection>
            </div>

            <div className="mt-6">
                <FormSection title="Actions">
                    <div className="flex items-center gap-2">
                        <Tooltip
                            title="Synchronizes tenant members with community membership"
                            hideIcon
                        >
                            <Button
                                loading={triggerTenantMemberSync.loading}
                                onClick={() => {
                                    if (communityId) {
                                        handleTriggerTenantMemberSync(
                                            parseInt(communityId)
                                        );
                                    }
                                }}
                            >
                                <RefreshCw />
                                Trigger Tenant member sync
                            </Button>
                        </Tooltip>
                        <Tooltip title="This action will go through all Tenant members and will add missing Users to Community as members. Users who are already in Community are not affected." />
                    </div>
                </FormSection>
            </div>
        </div>
    );
});

const getStatusText = (approved?: boolean) => {
    if (approved === undefined) {
        return 'Unknown';
    }
    return approved ? 'Approved' : 'Not Approved';
};

const formatDate = (dateString?: string) => {
    if (!dateString) {
        return 'N/A';
    }
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const extractDescriptionText = (description?: I18NText): string | undefined => {
    if (!description) {
        return undefined;
    }
    return (
        description.en ||
        description.de ||
        description.pl ||
        description.da ||
        description.nb ||
        ''
    );
};

export default CommunityDetails;
